import { Clinician, UserRole } from '../types';
import { supabase, isSupabaseAvailable, dbToClinician, clinicianToDb } from './supabaseClient';

// ============================================
// CLINICIAN SERVICE
// ============================================

const getCurrentUserRole = async () => {
  const { data: { user } } = await supabase!.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase!
    .from('clinicians')
    .select('role')
    .eq('user_id', user.id)
    .single();

  if (error) {
    console.error('Error fetching current clinician role:', error);
    return null;
  }

  return { userId: user.id, role: data.role as UserRole };
};

/**
 * Get all clinicians (active by default)
 */
export const getClinicians = async (includeInactive: boolean = false): Promise<Clinician[]> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return [];
  }

  try {
    const current = await getCurrentUserRole();
    if (!current) {
      console.warn('No authenticated user, cannot fetch clinicians');
      return [];
    }

    let query = supabase!
      .from('clinicians')
      .select('*')
      .order('name', { ascending: true });

    if (!includeInactive) {
      query = query.eq('active', true);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching clinicians:', error);
      return [];
    }

    return (data || []).map(dbToClinician);
  } catch (err) {
    console.error('Failed to fetch clinicians:', err);
    return [];
  }
};

export interface ClinicianWithStats extends Clinician {
  testsCount: number;
  lastActivity: string | null;
  createdCliniciansCount: number;
  maxClinicians: number | null;
  subscriptionTier: string;
  subscriptionStatus: string | null;
}

export const getCliniciansWithStats = async (includeInactive: boolean = false): Promise<ClinicianWithStats[]> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return [];
  }

  try {
    const current = await getCurrentUserRole();
    if (!current) {
      console.warn('No authenticated user, cannot fetch clinician stats');
      return [];
    }

    const { data: { session } } = await supabase!.auth.getSession();
    if (!session) {
      console.warn('No active session, cannot fetch clinician stats');
      return [];
    }

    const { data, error } = await supabase!.functions.invoke('get-users-management', {
      body: { includeInactive },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Error fetching clinicians with stats:', error);
      return [];
    }

    return (data?.clinicians || []) as ClinicianWithStats[];
  } catch (err) {
    console.error('Failed to fetch clinicians with stats:', err);
    return [];
  }
};

/**
 * Get a single clinician by ID
 */
export const getClinicianById = async (id: string): Promise<Clinician | null> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return null;
  }

  try {
    const current = await getCurrentUserRole();
    if (!current) {
      console.warn('No authenticated user, cannot fetch clinician');
      return null;
    }

    const { data, error } = await supabase!
      .from('clinicians')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching clinician:', error);
      return null;
    }

    return data ? dbToClinician(data) : null;
  } catch (err) {
    console.error('Failed to fetch clinician:', err);
    return null;
  }
};

/**
 * Get a clinician by email
 */
export const getClinicianByEmail = async (email: string): Promise<Clinician | null> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return null;
  }

  try {
    const current = await getCurrentUserRole();
    if (!current) {
      console.warn('No authenticated user, cannot fetch clinician by email');
      return null;
    }

    const { data, error } = await supabase!
      .from('clinicians')
      .select('*')
      .eq('email', email)
      .single();

    if (error) {
      console.error('Error fetching clinician by email:', error);
      return null;
    }

    return data ? dbToClinician(data) : null;
  } catch (err) {
    console.error('Failed to fetch clinician by email:', err);
    return null;
  }
};

/**
 * Create a new clinician
 */
export const createClinician = async (
  clinicianData: {
    name: string;
    email: string;
    password: string;
    role: UserRole;
    phone?: string;
    licenseNumber?: string;
    specialization?: string;
    avatarUrl?: string;
  }
): Promise<Clinician | null> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return null;
  }

  try {
    const current = await getCurrentUserRole();
    if (!current) {
      console.warn('No authenticated user, cannot create clinician');
      return null;
    }

    const { data: { session } } = await supabase!.auth.getSession();
    if (!session) {
      console.warn('No active session, cannot create clinician');
      return null;
    }

    const response = await supabase!.functions.invoke('create-clinician', {
      body: clinicianData,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (response.error) {
      console.error('Error creating clinician via function:', response.error);
      return null;
    }

    return response.data?.clinician ? dbToClinician(response.data.clinician) : null;
  } catch (err) {
    console.error('Failed to create clinician:', err);
    return null;
  }
};

/**
 * Update an existing clinician
 */
export const updateClinician = async (
  id: string,
  updates: Partial<Omit<Clinician, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<Clinician | null> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return null;
  }

  try {
    const current = await getCurrentUserRole();
    if (!current) {
      console.warn('No authenticated user, cannot update clinician');
      return null;
    }

    const dbUpdates: Record<string, unknown> = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.email !== undefined) dbUpdates.email = updates.email;
    if (updates.role !== undefined) dbUpdates.role = updates.role;
    if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl || null;
    if (updates.phone !== undefined) dbUpdates.phone = updates.phone || null;
    if (updates.licenseNumber !== undefined) dbUpdates.license_number = updates.licenseNumber || null;
    if (updates.specialization !== undefined) dbUpdates.specialization = updates.specialization || null;
    if (updates.clinicianType !== undefined) dbUpdates.clinician_type = updates.clinicianType || null;
    if (updates.maxClinicians !== undefined) dbUpdates.max_clinicians = updates.maxClinicians || null;
    if (updates.active !== undefined) dbUpdates.active = updates.active;
    if (updates.evolutivosEnabled !== undefined) dbUpdates.evolutivos_enabled = updates.evolutivosEnabled;

    const { data, error } = await supabase!
      .from('clinicians')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating clinician:', error);
      return null;
    }

    return data ? dbToClinician(data) : null;
  } catch (err) {
    console.error('Failed to update clinician:', err);
    return null;
  }
};

/**
 * Deactivate a clinician (soft delete)
 */
export const deactivateClinician = async (id: string): Promise<boolean> => {
  const result = await updateClinician(id, { active: false });
  return result !== null;
};

/**
 * Reactivate a clinician
 */
export const reactivateClinician = async (id: string): Promise<boolean> => {
  const result = await updateClinician(id, { active: true });
  return result !== null;
};

/**
 * Delete a clinician permanently
 */
export const deleteClinician = async (id: string): Promise<boolean> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return false;
  }

  try {
    const current = await getCurrentUserRole();
    if (!current) {
      console.warn('No authenticated user, cannot delete clinician');
      return false;
    }

    const { error } = await supabase!
      .from('clinicians')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting clinician:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Failed to delete clinician:', err);
    return false;
  }
};

/**
 * Search clinicians by name or email
 */
export const searchClinicians = async (query: string): Promise<Clinician[]> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return [];
  }

  try {
    const current = await getCurrentUserRole();
    if (!current) {
      console.warn('No authenticated user, cannot search clinicians');
      return [];
    }

    const { data, error } = await supabase!
      .from('clinicians')
      .select('*')
      .or(`name.ilike.%${query}%,email.ilike.%${query}%`)
      .eq('active', true)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error searching clinicians:', error);
      return [];
    }

    return (data || []).map(dbToClinician);
  } catch (err) {
    console.error('Failed to search clinicians:', err);
    return [];
  }
};

/**
 * Get clinicians by role
 */
export const getCliniciansByRole = async (role: UserRole): Promise<Clinician[]> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return [];
  }

  try {
    const current = await getCurrentUserRole();
    if (!current) {
      console.warn('No authenticated user, cannot fetch clinicians by role');
      return [];
    }

    const { data, error } = await supabase!
      .from('clinicians')
      .select('*')
      .eq('role', role)
      .eq('active', true)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching clinicians by role:', error);
      return [];
    }

    return (data || []).map(dbToClinician);
  } catch (err) {
    console.error('Failed to fetch clinicians by role:', err);
    return [];
  }
};
